import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@headlessui/react";
import Notice from "components/tw/notice";
import { errorTextCls, helpTextCls, inputCls, labelCls } from "components/tw/classes";
import { checkExternalNumber } from "services/orders.service";
import { vilniusYmd } from "utils/vilnius-date";

/** Same limit as the server (orders.externalNumber / files.externalNumber). */
export const EXTERNAL_NUMBER_MAX_LENGTH = 36;

type Match = { id: string; customerName: string; date: string | null };

type Props = {
  companyId: string;
  /** The order being edited, so it does not warn about itself. */
  orderId?: string;
  isExternal: boolean;
  onExternalChange: (value: boolean) => void;
  externalNumber: string;
  onExternalNumberChange: (value: string) => void;
  /** Validation message for the number, from the form. */
  numberError?: string;
};

/**
 * "Išorinė sutartis" — the contract was drawn up and signed outside the ERP,
 * and carries its own number. Its signed PDF is attached on the order page
 * afterwards instead of being generated.
 *
 * Warns — without blocking — when another order of the company already has the
 * same number: two customers' contracts can share one, but usually it means
 * the contract was imported twice.
 */
export default function ExternalContractFields({
  companyId,
  orderId,
  isExternal,
  onExternalChange,
  externalNumber,
  onExternalNumberChange,
  numberError,
}: Props) {
  const { t } = useTranslation();
  const [matches, setMatches] = useState<Match[]>([]);

  useEffect(() => {
    const number = externalNumber.trim();
    if (!isExternal || !number || number.length > EXTERNAL_NUMBER_MAX_LENGTH) {
      setMatches([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { matches: found } = await checkExternalNumber({
          companyId,
          number,
          excludeId: orderId,
        });
        if (!cancelled) setMatches(found || []);
      } catch (e) {
        // Only a warning: a failed lookup must not get in the way of saving.
        if (!cancelled) setMatches([]);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [companyId, orderId, isExternal, externalNumber]);

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        <Switch
          checked={isExternal}
          onChange={onExternalChange}
          className={`${
            isExternal ? "bg-indigo-600" : "bg-zinc-200 dark:bg-zinc-700"
          } relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors`}
        >
          <span className="sr-only">{t("order_external_contract")}</span>
          <span
            className={`${
              isExternal ? "translate-x-5" : "translate-x-1"
            } inline-block size-4 rounded-full bg-white transition`}
          />
        </Switch>
        <div>
          <p className="text-sm font-medium text-zinc-800 dark:text-zinc-100">
            {t("order_external_contract")}
          </p>
          <p className="mt-0.5 text-xs text-zinc-500">{t("order_external_contract_help")}</p>
        </div>
      </div>

      {isExternal && (
        <div>
          <label htmlFor="order-external-number" className={labelCls}>
            {t("order_external_number")}
          </label>
          <input
            id="order-external-number"
            type="text"
            autoComplete="off"
            maxLength={EXTERNAL_NUMBER_MAX_LENGTH}
            className={inputCls}
            value={externalNumber}
            onChange={(e) => onExternalNumberChange(e.target.value)}
            aria-invalid={numberError ? true : undefined}
          />
          {numberError ? (
            <p className={errorTextCls}>{numberError}</p>
          ) : (
            <p className={helpTextCls}>{t("order_external_number_help")}</p>
          )}
        </div>
      )}

      {isExternal && matches.length > 0 && (
        <Notice tone="warning">
          <p>{t("order_external_number_duplicate", { number: externalNumber.trim() })}</p>
          <ul className="list-disc pl-5">
            {matches.map((match) => (
              <li key={match.id}>
                {[match.customerName, vilniusYmd(match.date)].filter(Boolean).join(" · ")}
              </li>
            ))}
          </ul>
        </Notice>
      )}
    </div>
  );
}
